/**
 * /auth/callback?token=...
 * Lands here when the admin clicks the magic-link email.
 * POSTs the token to the Worker which:
 *   - validates it
 *   - bumps last_login_at on the admin
 *   - sets the signed session cookie via Set-Cookie
 *   - returns the admin record
 * Then we refresh the auth context and route to /admin.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE, useAuth } from '../lib/auth';

type State =
  | { kind: 'working' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

const FRIENDLY: Record<string, string> = {
  token_invalid: 'This sign-in link is not valid. Request a fresh one.',
  token_used: 'This sign-in link has already been used. Request a fresh one.',
  token_expired: 'This sign-in link has expired. Request a fresh one.',
  admin_not_found: 'That email is no longer on the admin list.',
  token_required: 'No sign-in token found in the URL.',
  server_error:
    "Something went wrong on our end. Take a breath and try again in a minute.",
};

export default function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState<State>({ kind: 'working' });

  useEffect(() => {
    const token = params.get('token') ?? '';
    if (!token) {
      setState({ kind: 'error', message: FRIENDLY.token_required });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/auth/callback?token=${encodeURIComponent(token)}`,
          { credentials: 'include' }
        );
        const data = (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          error?: string;
        };
        if (cancelled) return;
        if (res.ok && data.ok) {
          await refresh();
          if (cancelled) return;
          setState({ kind: 'success' });
          navigate('/admin', { replace: true });
        } else {
          setState({
            kind: 'error',
            message:
              FRIENDLY[data.error ?? ''] ??
              "Something went wrong on our end. Try again in a minute.",
          });
        }
      } catch {
        if (!cancelled) {
          setState({
            kind: 'error',
            message:
              "We couldn't reach the server. Check your connection and try again.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-full bg-cream-soft text-burgundy font-hanken flex items-center justify-center px-4">
      <div className="w-full max-w-[440px] bg-cream-bright border border-burgundy/[0.12] rounded-2xl p-8 text-center shadow-sm">
        {state.kind === 'working' && (
          <p className="text-burgundy-faint text-sm">Signing you in...</p>
        )}
        {state.kind === 'success' && (
          <p className="text-burgundy-faint text-sm">Redirecting to your inbox...</p>
        )}
        {state.kind === 'error' && (
          <>
            <h1 className="font-bagel text-[28px] text-burgundy mb-2">
              That did not work.
            </h1>
            <p className="text-burgundy-soft text-[14px] leading-relaxed mb-5">
              {state.message}
            </p>
            <Link
              to="/login"
              className="inline-block bg-pink text-cream-bright px-5 py-2.5 rounded-xl font-bold text-[14px] tracking-[0.3px] hover:bg-pink-deep transition-colors"
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
